import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getMyDossiers } from "../api/dossier/getMyDossiers";
import { qualifyNewLead } from "../api/user/qualifyNewLead";
import { getVehicleMakes } from "../api/vehicle/getVehicleMakes";
import { getVehicleModels } from "../api/vehicle/getVehicleModels";
import { getVehicleVariants } from "../api/vehicle/getVehicleVariants";
import OverwriteQuoteModal from "../components/modals/OverwriteQuoteModal";
import PageContainer from "../components/PageContainer";
import { getLatestOpenDossier } from "../helpers/dossiers";
import {
  buildQuoteQueryString,
  extractQuoteParameters,
} from "../helpers/quoteParams";
import {
  getRequiredVehicleData,
  resolveVehicleNames,
} from "../helpers/vehicleParams";
import { useQuoteLimit } from "../hooks/useQuoteLimit";
import { useAuth } from "../providers/AuthProvider";

const CreateQuoteRedirect = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getScopedPageUrl } = useAuth();

  // Extract quote parameters using centralized helper
  const quoteParams = extractQuoteParameters(searchParams);
  const { loadMakes, loadModels, loadVariants } =
    getRequiredVehicleData(quoteParams);

  const dossiersResponse = getMyDossiers();
  const { refetch: refetchDossiers } = dossiersResponse;
  const { mutateAsync: qualifyNewLeadAsync, isLoading: isQualifyingNewLead } =
    qualifyNewLead();
  const vehicleMakesResponse = getVehicleMakes({ disabled: !loadMakes });
  const vehicleModelsResponse = getVehicleModels(quoteParams.makeId || "", {
    disabled: !loadModels,
  });
  const vehicleVariantsResponse = getVehicleVariants(
    quoteParams.makeId || "",
    quoteParams.modelId || "",
    quoteParams.modelYear || "",
    { disabled: !loadVariants }
  );

  // Determine if all necessary data is loaded
  const isSuccess =
    dossiersResponse.isSuccess &&
    dossiersResponse.data &&
    (!loadMakes || vehicleMakesResponse.isSuccess) &&
    (!loadModels || vehicleModelsResponse.isSuccess) &&
    (!loadVariants || vehicleVariantsResponse.isSuccess);

  // Get the latest active dossier
  const latestOpenDossier = isSuccess
    ? getLatestOpenDossier(dossiersResponse.data)
    : null;

  // Use quote limit hook
  const {
    showOverwriteModal,
    handleQuoteAction,
    handleOverwriteConfirm,
    handleOverwriteCancel,
    oldestQuote,
    isLoading: quotesLoading,
    isSuccess: quotesSuccess,
  } = useQuoteLimit({
    dossierId: latestOpenDossier?.id || "",
    primaryQuoteId: latestOpenDossier?.opportunity?.primaryQuoteId || null,
    onRedirect: (overwriteQuoteId?: string) => {
      if (!latestOpenDossier) {
        navigate(getScopedPageUrl("home"), { replace: true });
        return;
      }

      // Resolve vehicle names using centralized helper
      const resolvedParams = resolveVehicleNames(quoteParams);
      resolvedParams.purchaseType = "new";
      resolvedParams.isCreateUrl = "true";

      // Add overwrite quote parameter if provided
      if (overwriteQuoteId) {
        resolvedParams.overwriteQuote = overwriteQuoteId;
      }

      // Build query string using centralized helper
      const redirectUrl = buildQuoteQueryString(resolvedParams);

      // Check if the dossier has a primary quote
      if (!latestOpenDossier.opportunity?.primaryQuoteId) {
        // No primary quote found, redirect to create quote page for this dossier
        navigate(
          `${getScopedPageUrl(
            "createQuote",
            latestOpenDossier.id
          )}${redirectUrl}`,
          { replace: true }
        );
        return;
      }

      // Redirect to clone the primary quote
      const primaryQuoteId = latestOpenDossier.opportunity.primaryQuoteId;
      navigate(
        `${getScopedPageUrl("cloneQuote", primaryQuoteId)}${redirectUrl}`,
        { replace: true }
      );
    },
    onCancel: () => navigate(getScopedPageUrl("home"), { replace: true }),
  });

  const handleQualifyNewLead = async () => {
    await qualifyNewLeadAsync();
    refetchDossiers();
  };

  useEffect(() => {
    // Wait for all necessary data to load
    if (isSuccess && latestOpenDossier && quotesSuccess) {
      handleQuoteAction();
    } else if (
      isSuccess &&
      !dossiersResponse.isFetching &&
      !latestOpenDossier &&
      !isQualifyingNewLead
    ) {
      // No active dossier, so qualify a new lead
      handleQualifyNewLead();
    }
  }, [isSuccess, quotesSuccess, latestOpenDossier, isQualifyingNewLead]);

  // Determine loading state based on what's needed
  const isLoading =
    dossiersResponse.isLoading ||
    (loadMakes && vehicleMakesResponse.isLoading) ||
    (loadModels && vehicleModelsResponse.isLoading) ||
    (loadVariants && vehicleVariantsResponse.isLoading) ||
    quotesLoading ||
    isQualifyingNewLead;

  return (
    <>
      <OverwriteQuoteModal
        isOpen={showOverwriteModal}
        onClose={handleOverwriteCancel}
        onConfirm={handleOverwriteConfirm}
        oldestQuote={oldestQuote}
      />
      <PageContainer loading={isLoading} hideContent>
        <></>
      </PageContainer>
    </>
  );
};

export default CreateQuoteRedirect;
